/**
 * TabContainer Component - Manages tabs and their content
 */

import { UIComponent, UIStyle, MouseEvent, Rect } from './UIComponent';
import { TabButton } from './Button';

export interface TabDefinition {
  id: string;
  label: string;
  shortcut?: string;  // e.g., "I" for inventory
  content: UIComponent;
}

export interface TabContainerStyle extends UIStyle {
  tabBarHeight?: number;
  tabGap?: number;
  contentPadding?: number;
  tabBarBackgroundColor?: string;
  contentBackgroundColor?: string;
}

export class TabContainer extends UIComponent {
  protected style: TabContainerStyle;
  private tabs: TabDefinition[] = [];
  private tabButtons: TabButton[] = [];
  private activeTabId: string = '';

  public onTabChange?: (tabId: string) => void;

  constructor(style: Partial<TabContainerStyle> = {}) {
    super(style);
    this.style = { ...this.getDefaultStyle(), ...style } as TabContainerStyle;
  }

  protected getDefaultStyle(): TabContainerStyle {
    return {
      ...super.getDefaultStyle(),
      width: 'fill',
      height: 'fill',
      tabBarHeight: 50,
      tabGap: 5,
      contentPadding: 15,
      tabBarBackgroundColor: '#0d0a08',
      contentBackgroundColor: '#1a1510',
      borderRadius: 0,
    };
  }

  setTabs(tabs: TabDefinition[]): void {
    this.tabs = tabs;
    this.tabButtons = [];

    for (const tab of tabs) {
      const button = new TabButton(tab.label, {
        fontSize: 14,
      });

      button.onActivate = () => {
        this.setActiveTab(tab.id);
      };

      this.tabButtons.push(button);
    }

    // Activate first tab by default
    if (tabs.length > 0 && !this.activeTabId) {
      this.activeTabId = tabs[0].id;
      this.tabButtons[0].setActive(true);
    }
  }

  getTabs(): TabDefinition[] {
    return this.tabs;
  }

  getActiveTabId(): string {
    return this.activeTabId;
  }

  setActiveTab(tabId: string): void {
    if (tabId === this.activeTabId) return;

    const tabIndex = this.tabs.findIndex(t => t.id === tabId);
    if (tabIndex < 0) return;

    this.activeTabId = tabId;

    // Update button states
    for (let i = 0; i < this.tabButtons.length; i++) {
      this.tabButtons[i].setActive(this.tabs[i].id === tabId);
    }

    if (this.onTabChange) {
      this.onTabChange(tabId);
    }
  }

  getActiveContent(): UIComponent | null {
    const tab = this.tabs.find(t => t.id === this.activeTabId);
    return tab?.content || null;
  }

  // Handle keyboard shortcuts
  handleKeyboardShortcut(key: string): boolean {
    const upperKey = key.toUpperCase();
    const tab = this.tabs.find(t => t.shortcut?.toUpperCase() === upperKey);

    if (tab) {
      this.setActiveTab(tab.id);
      return true;
    }

    return false;
  }

  measureSize(_ctx: CanvasRenderingContext2D): { width: number; height: number } {
    // Tab container typically fills parent
    return { width: 800, height: 600 };
  }

  protected renderContent(_ctx: CanvasRenderingContext2D): void {
    // Content is rendered in the overridden render method
  }

  layout(ctx: CanvasRenderingContext2D, availableRect: Rect): void {
    super.layout(ctx, availableRect);

    const tabBarHeight = this.style.tabBarHeight || 50;
    const tabGap = this.style.tabGap || 5;
    const contentPadding = this.style.contentPadding || 15;

    // Layout tab buttons
    let tabX = this.bounds.x + 10;
    const tabY = this.bounds.y + 10;

    for (const button of this.tabButtons) {
      const size = button.measureSize(ctx);
      button.layout(ctx, { x: tabX, y: tabY, width: size.width, height: size.height });
      tabX += size.width + tabGap;
    }

    // Layout active content
    const contentRect: Rect = {
      x: this.bounds.x + contentPadding,
      y: this.bounds.y + tabBarHeight + contentPadding,
      width: this.bounds.width - contentPadding * 2,
      height: this.bounds.height - tabBarHeight - contentPadding * 2,
    };

    const activeContent = this.getActiveContent();
    if (activeContent) {
      activeContent.layout(ctx, contentRect);
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    if (!this.visible) return;

    const tabBarHeight = this.style.tabBarHeight || 50;

    // Tab bar background
    ctx.fillStyle = this.style.tabBarBackgroundColor || '#0d0a08';
    ctx.fillRect(this.bounds.x, this.bounds.y, this.bounds.width, tabBarHeight);

    // Tab bar border
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(this.bounds.x, this.bounds.y + tabBarHeight);
    ctx.lineTo(this.bounds.x + this.bounds.width, this.bounds.y + tabBarHeight);
    ctx.stroke();

    // Content background
    ctx.fillStyle = this.style.contentBackgroundColor || '#1a1510';
    ctx.fillRect(
      this.bounds.x,
      this.bounds.y + tabBarHeight,
      this.bounds.width,
      this.bounds.height - tabBarHeight
    );

    // Render tab buttons
    for (const button of this.tabButtons) {
      button.render(ctx);
    }

    // Render active content
    const activeContent = this.getActiveContent();
    if (activeContent) {
      activeContent.render(ctx);
    }
  }

  handleMouseEvent(e: MouseEvent): boolean {
    if (!this.visible) return false;

    // Check tab buttons first
    for (const button of this.tabButtons) {
      if (button.handleMouseEvent(e)) {
        return true;
      }
    }

    // Check active content
    const activeContent = this.getActiveContent();
    if (activeContent && activeContent.handleMouseEvent(e)) {
      return true;
    }

    return false;
  }
}
